import React, { FC, Fragment } from 'react';
import { Avatar, Box, Divider, Grid, Link, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Organization } from 'models/organization';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import OpeningHours from './opening-hours.component';
import LabHolidays from './lab-holidays.component';
import LabContactDetails from './lab-contact-details.component';

type LabDetailsDialogProps = {
    lab?: Organization;
    open: boolean;
    onClose: () => void;
    isConnected?: boolean;
};

const LabDetailsDialog: FC<LabDetailsDialogProps> = ({ lab, open, onClose }) => {
    const { t: translate } = useTranslation(['common', 'organization']);

    return (
        <Fragment>
            {lab ? (
                <DHDialog
                    title={translate('organization:DETAILS_ABOUT_THE_LAB')}
                    maxWidth="lg"
                    fullWidth
                    open={open}
                    onClose={onClose}
                >
                    <Grid container sx={{ p: 2 }} spacing={2} alignItems="center">
                        <Box sx={{ display: 'flex', gap: 3 }}>
                            {lab.logoSignedUrl ? (
                                <Box
                                    component="img"
                                    src={lab.logoSignedUrl}
                                    alt={lab.name}
                                    sx={{
                                        width: 220,
                                        height: 220,
                                        objectFit: 'contain',
                                        borderRadius: 1.5,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        bgcolor: 'grey.50',
                                        p: 0.5,
                                        flexShrink: 0,
                                    }}
                                />
                            ) : (
                                <Avatar
                                    variant="rounded"
                                    sx={{
                                        width: 220,
                                        height: 220,
                                        fontSize: '1.75rem',
                                        bgcolor: 'primary.main',
                                        borderRadius: 1.5,
                                        flexShrink: 0,
                                    }}
                                >
                                    {lab.name.charAt(0).toUpperCase()}
                                </Avatar>
                            )}

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                    {lab.name}
                                </Typography>
                                <LabContactDetails contact={lab.contact} />
                            </Box>
                        </Box>
                    </Grid>

                    <Divider sx={{ mb: 3 }} />

                    {lab.longDescription && (
                        <>
                            <Box
                                sx={{
                                    mb: 3,
                                    p: 2,
                                    bgcolor: 'grey.50',
                                    borderRadius: 2,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    whiteSpace: 'pre-line',
                                }}
                            >
                                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                                    {lab.longDescription}
                                </Typography>
                            </Box>
                            <Divider sx={{ mb: 3 }} />
                        </>
                    )}

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={7}>
                            <OpeningHours currentLab={lab} />
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <LabHolidays
                                holidayDays={lab.holidayDays}
                                unavailableLabDates={lab.unavailableLabDates}
                                openingHours={lab.openingHours}
                            />
                        </Grid>
                    </Grid>
                </DHDialog>
            ) : undefined}
        </Fragment>
    );
};

export default LabDetailsDialog;
